// FILE: src/components/MemberDevotionals.jsx
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, query, orderBy, getDocs } from "firebase/firestore";

export default function MemberDevotionals() {
  const [devotionals, setDevotionals] = useState([]);
  const [devLoading, setDevLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Helper to format Firestore timestamps or ISO strings
  function formatTimestamp(ts) {
    if (!ts) return "";
    try {
      if (ts.toDate) {
        return ts.toDate().toLocaleDateString();
      }
      return new Date(ts).toLocaleDateString();
    } catch {
      return "";
    }
  }

  // Load published devotionals created in AdminDevotionals (once, no live stream)
  useEffect(() => {
    (async () => {
      setDevLoading(true);
      try {
        const qRef = query(
          collection(db, "devotionals"),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(qRef);
        const all = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        // Only show status === "Published"
        const published = all.filter((d) => d.status === "Published");
        setDevotionals(published);
      } catch (err) {
        console.error("Error loading devotionals for members:", err);
        setDevotionals([]);
      } finally {
        setDevLoading(false);
      }
    })();
  }, []);

  const latestId = devotionals[0]?.id;

  // 🔍 Filter devotionals by theme OR verse based on searchTerm
  const filteredDevotionals = devotionals.filter((d) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const theme = (d.theme || "").toLowerCase();
    const verse = (d.verse || "").toLowerCase();
    return theme.includes(term) || verse.includes(term);
  });

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">
          Devotionals
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Spend a few moments with God using these short readings prepared by The Equip Foundation.
        </p>
      </div>

      {/* Container card */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-slate-800">
              Latest Devotionals
            </h3>
            {devotionals.length > 0 && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                {devotionals.length} published
              </span>
            )}
          </div>

          {/* 🔍 Search box */}
          <div className="w-full sm:w-64">
            <label className="block text-[11px] font-medium text-slate-500 mb-1">
              Search by theme or scripture
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="e.g. Faith, John 3:16"
              className="w-full px-3 py-1.5 text-sm rounded-full border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Loading / empty / list */}
        {devLoading ? (
          <div className="mt-2 text-sm text-slate-500">
            Loading devotionals…
          </div>
        ) : devotionals.length === 0 ? (
          <div className="mt-2 text-sm text-slate-500">
            No published devotionals yet. Please check again soon.
          </div>
        ) : filteredDevotionals.length === 0 ? (
          <div className="mt-3 text-sm text-slate-500">
            No devotionals found matching{" "}
            <span className="font-semibold">"{searchTerm}"</span>. <br />
            Try another theme or scripture reference.
          </div>
        ) : (
          <>
            {/* Small result count */}
            {searchTerm.trim() && (
              <div className="text-xs text-slate-500 mb-2">
                Showing{" "}
                <span className="font-semibold">
                  {filteredDevotionals.length}
                </span>{" "}
                devotional{filteredDevotionals.length > 1 ? "s" : ""} for "
                {searchTerm}".
              </div>
            )}

            <ul className="mt-3 space-y-4">
              {filteredDevotionals.map((d) => {
                const dateLabel =
                  formatTimestamp(d.createdAt) || d.date || "";

                return (
                  <li
                    key={d.id}
                    className="p-4 rounded-2xl border border-slate-200 bg-slate-50/60 shadow-sm hover:shadow-md hover:bg-white transition"
                  >
                    {/* Header row: theme + date + badges */}
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="text-base sm:text-lg font-semibold text-slate-800">
                          {d.theme || "Devotional"}
                        </h4>
                        {d.id === latestId && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium uppercase tracking-wide">
                            New
                          </span>
                        )}
                      </div>

                      {dateLabel && (
                        <span className="text-xs px-2 py-1 rounded-full bg-slate-800/5 text-slate-500">
                          {dateLabel}
                        </span>
                      )}
                    </div>

                    {/* Verse */}
                    {d.verse && (
                      <div className="mb-2">
                        <div className="text-[11px] font-semibold tracking-wide uppercase text-indigo-600">
                          Scripture
                        </div>
                        <p className="mt-0.5 text-sm text-slate-700">
                          {d.verse}
                        </p>
                      </div>
                    )}

                    {/* Reflection */}
                    {d.reflection && (
                      <div className="mb-3">
                        <div className="text-[11px] font-semibold tracking-wide uppercase text-slate-500">
                          Reflection
                        </div>
                        <p className="mt-0.5 text-sm leading-relaxed text-slate-800">
                          {d.reflection}
                        </p>
                      </div>
                    )}

                    {/* Prayer */}
                    {d.prayer && (
                      <div className="pt-2 border-t border-slate-200">
                        <div className="text-[11px] font-semibold tracking-wide uppercase text-slate-500 mb-1">
                          Prayer
                        </div>
                        <p className="text-sm text-slate-700 italic">
                          {d.prayer}
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
